// 键盘操作（WCAG 2.1.1 键盘可用、2.4.7 焦点可见）：
//   1. trackInputModality()：按 Tab（或在非输入框里按方向键）时给 <html> 加 kbd-nav，按下鼠标 / 触屏就去掉。
//      焦点框只在 html.kbd-nav 下显示（样式见 App.vue「键盘焦点框」），鼠标点击不出任何框。
//      光靠 :focus-visible 不够：鼠标点进输入框时浏览器也会算 :focus-visible。
//   2. v-kbd-toggle：挂在一组 div / p 做的切换按钮外层，让它们能用键盘操作。
//      v-kbd-toggle="{ item: '.range-option', active: 'range-option-active' }"
//        active 可以是数组：['p1Active', 'p2Active', 'p3Active']
//        单选（默认）：整组是一个 Tab 停靠点（停在选中的那个上），左右 / 上下方向键切换并立即选中，
//        Home / End 跳到第一个 / 最后一个，回车 / 空格选中。
//      v-kbd-toggle="{ item: '.social-platforms__item', active: 'is-selected', multi: true }"
//        多选（平台按钮这类可以多选的）：每个按钮各是一个 Tab 停靠点，回车 / 空格切换，方向键只移动焦点。
//      inset: true：按钮在会裁切的容器里（如横向滚动的平台按钮），焦点框画在按钮内侧
//      选中 / 取消仍然走按钮原来的 @click（这里对它 .click()），组件代码不用改。

const KBD_CLASS = "kbd-nav";
const NAV_KEYS = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];

function isTextField(node) {
  if (!node || !node.tagName) return false;
  if (node.isContentEditable) return true;
  const tag = node.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag !== "INPUT") return false;
  return !["checkbox", "radio", "button", "submit", "reset", "range", "color", "file"].includes(node.type);
}

export function trackInputModality() {
  const root = document.documentElement;
  window.addEventListener(
    "keydown",
    (e) => {
      if (e.key === "Tab" || (NAV_KEYS.includes(e.key) && !isTextField(e.target))) root.classList.add(KBD_CLASS);
    },
    true
  );
  window.addEventListener("pointerdown", () => root.classList.remove(KBD_CLASS), true);
}

function options(binding) {
  const v = binding.value || {};
  const active = Array.isArray(v.active) ? v.active : v.active ? [v.active] : [];
  return { item: v.item, active, multi: !!v.multi, inset: !!v.inset, disabled: v.disabled || "" };
}

function itemsOf(el, opts) {
  if (!opts.item) return [];
  return Array.from(el.querySelectorAll(opts.item)).filter(
    (n) => !(opts.disabled && n.classList.contains(opts.disabled)) && n.getAttribute("aria-disabled") !== "true"
  );
}

// active：选中时的 class，可以给一个或几个（每项各用一个的，如 p1Active / p2Active / p3Active）；
// class 挂在按钮自己或它里面的元素上都算（如图例按钮里色块的 opacity1）
function isActive(node, opts) {
  return opts.active.some((cls) => node.classList.contains(cls) || !!node.querySelector("." + cls));
}

function setAttr(node, name, value) {
  if (node.getAttribute(name) !== value) node.setAttribute(name, value);
}

// 按当前选中状态补上 role / aria / tabindex
function sync(el, opts) {
  const items = itemsOf(el, opts);
  if (!items.length) return;
  // inset：按钮在会裁切的容器里（横向滚动条等），焦点框改画在按钮内侧（样式见 App.vue .kbd-ring-inset）
  if (opts.inset) items.forEach((node) => node.classList.contains("kbd-ring-inset") || node.classList.add("kbd-ring-inset"));
  if (opts.multi) {
    setAttr(el, "role", "group");
    items.forEach((node) => {
      setAttr(node, "role", "button");
      setAttr(node, "tabindex", "0");
      if (opts.active.length) setAttr(node, "aria-pressed", String(isActive(node, opts)));
    });
    return;
  }
  setAttr(el, "role", "radiogroup");
  // 单选组只留一个 Tab 停靠点：选中的那个；一个都没选中时停在第一个
  const current = items.find((node) => isActive(node, opts)) || items[0];
  items.forEach((node) => {
    setAttr(node, "role", "radio");
    setAttr(node, "aria-checked", String(isActive(node, opts)));
    setAttr(node, "tabindex", node === current ? "0" : "-1");
  });
}

function onKeydown(el, e) {
  const opts = el.__kbdToggle.opts;
  const items = itemsOf(el, opts);
  const from = items.find((node) => node === e.target || node.contains(e.target));
  if (!from) return;
  if (e.key === "Enter" || e.key === " ") {
    // 原生 <button> 自己会响应回车 / 空格，再 click 一次就触发两遍
    if (from.tagName === "BUTTON") return;
    e.preventDefault();
    from.click();
    return;
  }
  if (!NAV_KEYS.includes(e.key)) return;
  e.preventDefault();
  const i = items.indexOf(from);
  let next = i;
  if (e.key === "Home") next = 0;
  else if (e.key === "End") next = items.length - 1;
  else if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % items.length;
  else next = (i - 1 + items.length) % items.length;
  const target = items[next];
  if (!target || target === from) return;
  if (!opts.multi) setAttr(target, "tabindex", "0");
  target.focus();
  // 单选：方向键即选中（同原生单选按钮）；多选只移动焦点，回车 / 空格再切换
  if (!opts.multi) target.click();
}

export const kbdToggle = {
  inserted(el, binding) {
    const state = { opts: options(binding) };
    state.keydown = (e) => onKeydown(el, e);
    el.addEventListener("keydown", state.keydown);
    // 选中状态 / 选项列表变了（包括子组件自己重渲染）就重新补 aria / tabindex。只看 class 和子节点，
    // sync 自己改的 role / aria / tabindex 不会再触发
    state.observer = new MutationObserver(() => sync(el, state.opts));
    state.observer.observe(el, { subtree: true, childList: true, attributes: true, attributeFilter: ["class"] });
    el.__kbdToggle = state;
    sync(el, state.opts);
  },
  componentUpdated(el, binding) {
    if (!el.__kbdToggle) return;
    el.__kbdToggle.opts = options(binding);
    sync(el, el.__kbdToggle.opts);
  },
  unbind(el) {
    const state = el.__kbdToggle;
    if (!state) return;
    el.removeEventListener("keydown", state.keydown);
    state.observer.disconnect();
    delete el.__kbdToggle;
  },
};

// v-kbd-button：单个 div 做的按钮（如筛选栏的搜索按钮）—— 能 Tab 到，回车 / 空格触发 click
export const kbdButton = {
  inserted(el) {
    if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "0");
    if (!el.hasAttribute("role")) el.setAttribute("role", "button");
    el.__kbdButton = (e) => {
      if (e.target !== el || (e.key !== "Enter" && e.key !== " ")) return;
      e.preventDefault();
      el.click();
    };
    el.addEventListener("keydown", el.__kbdButton);
  },
  unbind(el) {
    if (el.__kbdButton) el.removeEventListener("keydown", el.__kbdButton);
    delete el.__kbdButton;
  },
};
